import { Suspense, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bookmark,
  Check,
  ExternalLink,
  Info,
  Play,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ShareButton } from "./ShareButton";
import { useMediaDetail } from "@/hooks/useMediaDetail";
import { useUserStatus } from "@/hooks/useUserStatus";
import type { MediaDetail as MediaDetailType } from "@/types/media";
import type { EpisodeRating } from "@/lib/episodes";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { MediaCard } from "./MediaCard";
import { EpisodeHeatmap } from "./EpisodeHeatmap";
import { ScoreBadge, tierName } from "./ScoreBadge";
import { AmbientGlow } from "./AmbientGlow";
import { ScrollRail } from "./ScrollRail";
import { isRankable, trustedScore } from "@/lib/trust";
import { listLabel } from "@/lib/ranksOn";
import { displayYear } from "@/lib/mediaFormat";
import { tmdbImage, tmdbSrcSet } from "@/lib/tmdbImage";
import { WhereToWatch } from "./WhereToWatch";
import { titleProse, normalizedSources, runtimeLabel, tenPoint } from "@/lib/titleProse";
import { appearsInQueryOptions } from "@/hooks/useMediaDetail";
import { themeForKeyword } from "@/lib/taxonomy";
import { deriveOrigins } from "@/lib/origins";
import {
  isNotInterested,
  primaryOf,
  recordForNotInterested,
  recordForSentiment,
  recordForWant,
  recordForWatched,
  sentimentOf,
  PRIMARY_HEX,
  PRIMARY_LABEL,
  SENTIMENT_HEX,
  SENTIMENT_LABEL,
  type PrimaryKey,
  type SentimentKey,
} from "@/lib/userStatus";

function fmtMoney(n?: number) {
  if (!n) return undefined;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

// ISO-639-1 code → full English language name ("en" → "English"). Falls back to
// the upper-cased code for anything Intl can't resolve.
let langNames: Intl.DisplayNames | null = null;
function languageName(code?: string): string {
  if (!code) return "";
  try {
    langNames ??= new Intl.DisplayNames(["en"], { type: "language" });
    return langNames.of(code.toLowerCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

function PersonName({ name, personId }: { name: string; personId?: number }) {
  if (personId) {
    return (
      <Link
        to="/person/$id"
        params={{ id: String(personId) }}
        className="truncate text-text-bright hover:text-primary"
      >
        {name}
      </Link>
    );
  }
  return <span className="truncate text-text-bright">{name}</span>;
}

// A metadata chip that links straight into the homepage grid filtered by that single
// facet — one tap opens "more like this" along that axis (genre / theme / origin).
function FacetLink({
  search,
  children,
}: {
  search: Record<string, string>;
  children: React.ReactNode;
}) {
  return (
    <Link
      to="/"
      search={search}
      className="rounded-[4px] border border-border bg-panel px-2 py-0.5 text-[12px] font-semibold tracking-[-0.01em] text-text-muted transition-colors hover:border-primary hover:text-primary"
    >
      {children}
    </Link>
  );
}

// Section headings carry the weight instead of shouting in 11px uppercase
// monospace. Monospace is reserved for numbers and machine facts below.
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2.5 text-[18px] font-black leading-none tracking-[-0.02em] text-text-bright">
      {children}
    </h2>
  );
}

// The same voice one step down, for the 300px sidebar panels.
function PanelHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-[15px] font-black leading-none tracking-[-0.02em] text-text-bright">
      {children}
    </h2>
  );
}

// Two-axis status UI: pick a primary state (Want to Watch | Watched), and once
// something is Watched a sentiment row appears (Liked it / Not for me). Sentiment
// is never clobbered by re-tapping Watched. "Never show this" is the quiet escape
// hatch underneath — a hard "won't watch" that keeps the deck honest.
function StatusControls({ detail }: { detail: MediaDetailType }) {
  const { statuses, recordStatus } = useUserStatus();
  const rec = statuses[detail.id];
  const primary = primaryOf(rec);
  const sentiment = sentimentOf(rec);
  const rejected = isNotInterested(rec);

  const setPrimary = (key: PrimaryKey) => {
    if (primary === key) {
      recordStatus(detail.id, null); // tap again to clear
    } else {
      recordStatus(detail.id, key === "want" ? recordForWant() : recordForWatched(rec), detail);
    }
  };

  const setSentiment = (s: SentimentKey) => {
    recordStatus(detail.id, recordForSentiment(rec, s), detail);
  };

  const toggleNotInterested = () => {
    recordStatus(detail.id, rejected ? null : recordForNotInterested(), detail);
  };

  const primaryBtn = (key: PrimaryKey, icon: React.ReactNode) => {
    const active = primary === key;
    return (
      <button
        type="button"
        onClick={() => setPrimary(key)}
        aria-pressed={active}
        className={
          "flex items-center justify-center gap-1.5 rounded-[4px] border px-2 py-2 text-[13px] font-bold tracking-[-0.01em] transition-colors " +
          (active
            ? "border-border-strong bg-background text-text-bright"
            : "border-border bg-transparent text-text-muted hover:border-border-strong hover:text-text-bright")
        }
        style={active ? { borderColor: PRIMARY_HEX[key] } : undefined}
      >
        <span style={active ? { color: PRIMARY_HEX[key] } : undefined}>{icon}</span>
        {PRIMARY_LABEL[key]}
      </button>
    );
  };

  return (
    <div className="rounded-[5px] border border-border bg-panel p-3">
      <PanelHeading>Your status</PanelHeading>
      <div className="grid grid-cols-2 gap-1.5">
        {primaryBtn("want", <Bookmark className="h-3.5 w-3.5" />)}
        {primaryBtn("watched", <Check className="h-3.5 w-3.5" />)}
      </div>

      {primary === "watched" && (
        <div className="mt-2">
          <div className="mb-1 text-[13px] font-semibold tracking-[-0.01em] text-text-dim">
            How was it?
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {(["liked", "disliked"] as const).map((s) => {
              const active = sentiment === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSentiment(s)}
                  aria-pressed={active}
                  className={
                    "flex items-center justify-center gap-1.5 rounded-[4px] border px-2 py-1.5 text-[13px] font-bold tracking-[-0.01em] transition-colors " +
                    (active
                      ? "bg-background text-text-bright"
                      : "border-border bg-transparent text-text-muted hover:border-border-strong hover:text-text-bright")
                  }
                  style={
                    active ? { borderColor: SENTIMENT_HEX[s], color: SENTIMENT_HEX[s] } : undefined
                  }
                >
                  {s === "liked" ? (
                    <ThumbsUp className="h-3 w-3" />
                  ) : (
                    <ThumbsDown className="h-3 w-3" />
                  )}
                  {SENTIMENT_LABEL[s]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between">
        <button
          type="button"
          onClick={toggleNotInterested}
          aria-pressed={rejected}
          className={
            "cursor-pointer text-[12px] font-semibold tracking-[-0.01em] transition-colors " +
            (rejected ? "text-[#c75d6e]" : "text-text-dim hover:text-text-muted")
          }
        >
          {rejected ? "✕ Never show this (undo)" : "Never show this"}
        </button>
        {(primary || rejected) && (
          <button
            type="button"
            onClick={() => recordStatus(detail.id, null)}
            className="cursor-pointer text-[12px] font-semibold tracking-[-0.01em] text-text-dim hover:text-text-muted"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

function DetailInner({
  detail,
  episodeRatings,
  colorA,
  colorB,
}: {
  detail: MediaDetailType;
  episodeRatings?: EpisodeRating[];
  colorA?: string | null;
  colorB?: string | null;
}) {
  const { ratings, facts, external } = detail;
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const typeLabel = detail.mediaType === "movie" ? "Movie" : "TV";
  const length =
    detail.mediaType === "movie"
      ? detail.runtime
        ? runtimeLabel(detail.runtime)
        : undefined
      : detail.numberOfSeasons
        ? `${detail.numberOfSeasons} season${detail.numberOfSeasons === 1 ? "" : "s"}${
            detail.numberOfEpisodes
              ? ` · ${detail.numberOfEpisodes} episode${detail.numberOfEpisodes === 1 ? "" : "s"}`
              : ""
          }`
        : undefined;

  const meta = [displayYear(detail), typeLabel, length, detail.certification].filter(Boolean);
  // Derived from language alone (no ISO country codes available on the client-fetched
  // detail payload) — covers the distinctive, language-first buckets (Korean, Japanese,
  // Chinese, Indian, Spanish, French); English-language titles resolve to no bucket here.
  const origin = deriveOrigins(facts.originalLanguage, undefined)[0];

  // One blend, read three times: the hero lockup, the Ratings section, and the
  // prose. Undefined when it rests on one source or a handful of votes, so the
  // badge, the tier name and the sentence all go together.
  const balasaur = trustedScore({ ratings, voteCount: detail.voteCount });

  // Deterministic data-prose, composed from what this database holds. See
  // src/lib/titleProse.ts for why this exists and what it is allowed to say.
  const prose = titleProse({
    mediaType: detail.mediaType,
    title: detail.title,
    year: detail.year,
    genres: detail.genres,
    origins: detail.origins,
    streaming: detail.streaming,
    runtime: detail.runtime,
    numberOfSeasons: detail.numberOfSeasons,
    numberOfEpisodes: detail.numberOfEpisodes,
    completionStatus: facts.status,
    awardWinner: detail.awardWinner,
    awardNominee: detail.awardNominee,
    voteCount: detail.voteCount,
    // Strip an untrusted blend; never add one the payload did not carry.
    ratings: { ...ratings, balasaur: ratings.balasaur === undefined ? undefined : balasaur },
    franchise: detail.context?.franchise,
  });
  const sourceBars = normalizedSources(ratings);
  // A list position is a promise the title belongs there. One that no longer
  // passes the trust rule (one source, a handful of votes, a trailer) keeps
  // its page but loses every "#N on" claim, here and in the closing rail.
  const rankable = isRankable({
    ratings,
    voteCount: detail.voteCount,
    mediaType: detail.mediaType,
    title: detail.title,
    genres: detail.genres,
    filmLengthMinutes: detail.mediaType === "movie" ? detail.runtime : null,
  });

  return (
    <article>
      {/* Hero */}
      <header className="relative">
        <div className="absolute inset-x-0 top-0 h-[300px] overflow-hidden bg-panel md:h-[440px]">
          {detail.backdropUrl && (
            <img
              src={tmdbImage(detail.backdropUrl, "w1280")}
              srcSet={tmdbSrcSet(detail.backdropUrl, [
                { w: 780, size: "w780" },
                { w: 1280, size: "w1280" },
              ])}
              sizes="100vw"
              alt=""
              decoding="async"
              fetchPriority="high"
              className="absolute inset-0 h-full w-full object-cover opacity-50"
            />
          )}
          {/* Scrim: darkens the lower half (where the title sits) so it stays
              legible over any backdrop, bright or dark. */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/30" />
        </div>

        <div className="relative isolate z-10 mx-auto max-w-[1100px] px-4 pt-[168px] md:pt-[260px]">
          {/* The poster's own light, thrown across the top of the page. It is
              mounted inside this z-10 column rather than under the backdrop
              block, because the backdrop is opaque: a glow behind it would be
              invisible for 440px and then appear all at once at its bottom
              edge, which is the horizontal seam the first version drew. The box
              is pushed down to start at the poster, so the light is behind the
              poster, title and score instead of over the banner. */}
          <AmbientGlow
            colorA={colorA}
            colorB={colorB}
            className="bottom-0 top-[120px] h-auto md:top-[190px]"
          />
          <div className="flex flex-col gap-5 md:flex-row md:items-end">
            <div className="w-[160px] shrink-0 overflow-hidden rounded-[8px] border border-border bg-panel shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] md:w-[220px]">
              <div className="aspect-[2/3] w-full">
                {detail.posterUrl ? (
                  <img
                    src={tmdbImage(detail.posterUrl, "w342")}
                    srcSet={tmdbSrcSet(detail.posterUrl, [
                      { w: 342, size: "w342" },
                      { w: 500, size: "w500" },
                    ])}
                    sizes="(max-width: 768px) 160px, 220px"
                    alt={detail.title}
                    width={342}
                    height={513}
                    fetchPriority="high"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[12px] font-semibold text-text-dim">
                    No art
                  </div>
                )}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                {/* Title and score are one lockup. The score used to sit eight
                    blocks down the page, which on a phone is the third screen:
                    the one thing here that no other site has was the last thing
                    a visitor saw. Below md the h1 owns the whole line and the
                    score drops under it; above md they share a line and the
                    title absorbs whatever width the score leaves. */}
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-3">
                  <h1 className="basis-full text-[30px] font-black leading-[1.08] tracking-[-0.02em] text-text-bright md:basis-auto md:text-[44px] [text-shadow:_0_1px_3px_rgba(0,0,0,0.55)]">
                    {detail.title}
                  </h1>
                  {/* No score, no lockup. A title with nothing to blend says so
                      once, in Ratings, rather than twice. */}
                  {balasaur !== undefined && (
                    <div className="flex shrink-0 items-center gap-2.5">
                      <ScoreBadge score={balasaur} size="lg" />
                      <div>
                        <div className="font-mono text-[11px] uppercase tracking-wider text-text-muted">
                          Balasaur Score
                        </div>
                        <div className="mt-0.5 text-[15px] font-black leading-none tracking-[-0.02em] text-text-bright md:text-[17px]">
                          {tierName(balasaur)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-1 shrink-0">
                  <ShareButton title={detail.title} />
                </div>
              </div>
              {meta.length > 0 && (
                <p className="mt-2 font-mono text-[12px] tabular-nums tracking-wider text-text-muted">
                  {meta.join(" · ")}
                </p>
              )}
              {detail.genres.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {detail.genres.map((g) => (
                    <FacetLink key={g} search={{ genres: g }}>
                      {g}
                    </FacetLink>
                  ))}
                </div>
              )}
              {detail.trailer && (
                <button
                  type="button"
                  onClick={() => setTrailerOpen(true)}
                  className="mt-4 inline-flex items-center gap-2 rounded-[5px] border border-border-strong bg-panel px-3 py-2 text-[14px] font-bold tracking-[-0.01em] text-text-bright transition-colors hover:bg-background"
                >
                  <Play className="h-3.5 w-3.5" />
                  Watch trailer
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Body. Someone who arrived from a "where to watch X" search needs the
          answer on the first screen, not below eight editorial sections, so on
          mobile that one panel is lifted above the main column. The rest of the
          sidebar (status, facts, links) has no claim on the first screen and now
          follows the page instead of burying it. The aside is display:contents
          below md, which makes its panels direct flex children here so a single
          one of them can be ordered without rendering it twice. On md and up it
          is a plain right sidebar again, in source order. */}
      <div className="mx-auto mt-8 flex max-w-[1100px] flex-col gap-6 px-4 pb-16 md:grid md:grid-cols-[1fr_300px]">
        {/* MAIN */}
        <div className="min-w-0 space-y-6">
          {/* Our read first, the distributor's synopsis second. The prose is
              composed from this database (score provenance, source
              disagreement, vote weight, shape, availability), so the page
              leads with something no other site holding the same API data
              can print. */}
          {prose && (
            <section>
              <SectionHeading>The read</SectionHeading>
              <p className="text-[15px] leading-relaxed text-text-bright">{prose}</p>
              {rankable && <RanksOn key={detail.id} mediaId={detail.id} title={detail.title} />}
            </section>
          )}

          {detail.overview && (
            <section>
              <SectionHeading>Synopsis</SectionHeading>
              <p className="text-[15px] leading-relaxed text-text">{detail.overview}</p>
            </section>
          )}

          <section>
            <SectionHeading>Ratings</SectionHeading>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(() => {
                // The unified blend, same as the card badges (per-source tiles
                // follow). Click for the breakdown — the score earns trust by
                // showing its math, and it explains why our 0–100 sits next to
                // IMDb's 0–10 and RT's %.
                if (balasaur === undefined) return null;
                const sources: { label: string; value?: string; weight: string }[] = [
                  {
                    label: "IMDb",
                    value: ratings.imdb !== undefined ? `${tenPoint(ratings.imdb)}/10` : undefined,
                    weight: "25%",
                  },
                  {
                    label: "Rotten Tomatoes",
                    value:
                      ratings.rottenTomatoes !== undefined
                        ? `${ratings.rottenTomatoes}%`
                        : undefined,
                    weight: "12.5%",
                  },
                  {
                    label: "Metacritic",
                    value:
                      ratings.metacritic !== undefined ? `${ratings.metacritic}/100` : undefined,
                    weight: "12.5%",
                  },
                  {
                    label: "TMDB",
                    value: ratings.tmdb !== undefined ? `${tenPoint(ratings.tmdb)}/10` : undefined,
                    weight: "10%",
                  },
                ];
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="cursor-pointer rounded-[5px] border border-border bg-panel px-3 py-2.5 text-left transition-colors hover:border-border-strong"
                      >
                        <div className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-text-dim">
                          Balasaur Score
                          <Info className="h-2.5 w-2.5" aria-hidden="true" />
                        </div>
                        <div className="mt-1">
                          <ScoreBadge score={balasaur} size="md" />
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-72 border-border bg-panel p-3 text-foreground"
                    >
                      <p className="text-[14px] font-black tracking-[-0.02em] text-text-bright">
                        How this score works
                      </p>
                      <p className="mt-1.5 text-[12px] leading-relaxed text-text-muted">
                        One 0–100 blend of every rating source we have, weighted by reliability and
                        renormalized when a source is missing.
                      </p>
                      <ul className="mt-2.5 space-y-1">
                        {sources.map((s) => (
                          <li
                            key={s.label}
                            className="flex items-center justify-between font-mono text-[11px] tabular-nums"
                          >
                            <span className={s.value ? "text-text-bright" : "text-text-dim"}>
                              {s.label}
                              <span className="ml-1.5 text-[11px] text-text-dim">{s.weight}</span>
                            </span>
                            <span className={s.value ? "text-text-bright" : "text-text-dim"}>
                              {s.value ?? "—"}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2.5 border-t border-border pt-2 text-[11px] leading-relaxed text-text-dim">
                        User ratings join the blend (at 50%) as the community grows.
                      </p>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>

            {/* Four sources on one 0 to 100 axis. Tiles showed four numbers in
                four different units, which hid the only thing worth seeing:
                whether the sources agree. Holding all four normalized is the
                thing a competitor mirroring the same API cannot do. */}
            {sourceBars.length > 0 ? (
              <div className="mt-3 space-y-1.5">
                {sourceBars.map((src) => (
                  <div key={src.label} className="flex items-center gap-2.5">
                    <span className="w-[104px] shrink-0 font-mono text-[11px] uppercase tracking-wider text-text-dim">
                      {src.label}
                    </span>
                    <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-border">
                      <span
                        className="block h-full rounded-full bg-rating"
                        style={{ width: `${Math.max(2, Math.min(100, src.pct))}%` }}
                      />
                    </span>
                    <span className="w-[68px] shrink-0 text-right font-mono text-[11px] tabular-nums text-text-bright">
                      {src.shown}
                    </span>
                  </div>
                ))}
                {typeof detail.voteCount === "number" && detail.voteCount > 0 && (
                  <p className="font-mono text-[12px] tabular-nums text-text-dim">
                    {detail.voteCount.toLocaleString("en-US")} audience votes
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-3 text-[13px] text-text-dim">No ratings available yet.</div>
            )}
          </section>

          {/* TV seasons are a sequence, so they get a table. Half the catalog
              is TV and this was previously not rendered at all. */}
          {detail.mediaType === "tv" && (detail.seasons?.length ?? 0) > 0 && (
            <section>
              <SectionHeading>Seasons</SectionHeading>
              <div className="overflow-hidden rounded-[5px] border border-border">
                <table className="w-full border-collapse">
                  <tbody>
                    {detail
                      .seasons!.filter((sn) => sn.seasonNumber > 0)
                      .map((sn) => (
                        <tr
                          key={sn.seasonNumber}
                          className="border-b border-border last:border-b-0"
                        >
                          <td className="px-3 py-2 font-mono text-[12px] tabular-nums text-text-dim">
                            {sn.seasonNumber}
                          </td>
                          <td className="min-w-0 px-1 py-2 text-[13px] text-text-bright">
                            <span className="block truncate">{sn.name}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[11px] tabular-nums text-text-muted">
                            {sn.episodeCount > 0 && `${sn.episodeCount} ep`}
                            {sn.airDate && (
                              <span className="ml-2 text-text-dim">{sn.airDate.slice(0, 4)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {detail.crew.length > 0 && (
            <section>
              <SectionHeading>Key crew</SectionHeading>
              <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                {detail.crew.map((p, i) => (
                  <li key={`${p.name}-${p.role}-${i}`} className="flex justify-between text-[13px]">
                    <PersonName name={p.name} personId={p.personId} />
                    <span className="text-[12px] text-text-dim">{p.role}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {detail.cast.length > 0 && (
            <section>
              <SectionHeading>Cast</SectionHeading>
              <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                {detail.cast.map((p, i) => (
                  <li key={`${p.name}-${i}`} className="flex justify-between gap-3 text-[13px]">
                    <PersonName name={p.name} personId={p.personId} />
                    <span className="truncate text-[12px] text-text-muted">{p.role}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {episodeRatings && episodeRatings.length > 0 && (
            <EpisodeHeatmap rows={episodeRatings} seasons={detail.seasons ?? []} />
          )}

          {detail.images && detail.images.length > 0 && (
            <section>
              <SectionHeading>Stills</SectionHeading>
              <ScrollRail className="snap-x snap-mandatory gap-2">
                {detail.images.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    className="group relative h-[140px] shrink-0 snap-start overflow-hidden rounded-[6px] border border-border bg-panel md:h-[170px]"
                    aria-label={`Open still ${i + 1}`}
                  >
                    <img
                      src={tmdbImage(src, "w500")}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-auto object-cover transition-opacity group-hover:opacity-90"
                    />
                  </button>
                ))}
              </ScrollRail>
            </section>
          )}

          {detail.related && detail.related.length > 0 && (
            <section>
              <SectionHeading>
                {detail.mediaType === "tv" ? "Shows" : "Movies"} like {detail.title}
              </SectionHeading>
              <ScrollRail className="gap-3">
                {detail.related.map((it) => (
                  <div key={it.id} className="w-[118px] shrink-0 md:w-[132px]">
                    <MediaCard item={it} />
                  </div>
                ))}
              </ScrollRail>
            </section>
          )}

          {detail.relatedCross && detail.relatedCross.length > 0 && (
            <section>
              <SectionHeading>
                {detail.mediaType === "tv" ? "Movies" : "Shows"} like {detail.title}
              </SectionHeading>
              <ScrollRail className="gap-3">
                {detail.relatedCross.map((it) => (
                  <div key={it.id} className="w-[118px] shrink-0 md:w-[132px]">
                    <MediaCard item={it} />
                  </div>
                ))}
              </ScrollRail>
            </section>
          )}

          {detail.keywords && detail.keywords.length > 0 && (
            <section>
              <SectionHeading>Themes</SectionHeading>
              <div className="flex flex-wrap gap-1.5">
                {detail.keywords.map((k) => {
                  const theme = themeForKeyword(k);
                  // Only keywords that map to a real theme facet are clickable — an
                  // unrecognized raw keyword has nothing to filter into, so it stays plain.
                  return theme ? (
                    <FacetLink key={k} search={{ themes: theme }}>
                      {k}
                    </FacetLink>
                  ) : (
                    <span
                      key={k}
                      className="rounded-[4px] border border-border bg-panel px-2 py-0.5 text-[12px] font-semibold tracking-[-0.01em] text-text-dim"
                    >
                      {k}
                    </span>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* SIDE */}
        <aside className="contents md:block md:space-y-4">
          <StatusControls detail={detail} />

          {/* empty:hidden so a title with no provider data (WhereToWatch renders
              nothing) does not leave a gap at the top of a phone screen. */}
          <div className="order-first empty:hidden">
            <WhereToWatch detail={detail} />
          </div>

          <div className="rounded-[5px] border border-border bg-panel p-3">
            <PanelHeading>Facts</PanelHeading>
            <dl className="space-y-1.5 text-[12px]">
              {fmtMoney(facts.budget) && <FactRow k="Budget" v={fmtMoney(facts.budget)!} mono />}
              {fmtMoney(facts.revenue) && (
                <FactRow k="Box office" v={fmtMoney(facts.revenue)!} mono />
              )}
              {facts.originalLanguage && (
                <FactRow k="Language" v={languageName(facts.originalLanguage)} />
              )}
              {origin && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-text-dim">Origin</dt>
                  <dd>
                    <FacetLink search={{ origins: origin }}>{origin}</FacetLink>
                  </dd>
                </div>
              )}
              {/* Production countries and companies were removed: they
                  duplicated Origin and changed nothing a viewer decides.
                  Status only earns its row while a title is unreleased. */}
              {facts.status && facts.status !== "Released" && facts.status !== "Ended" && (
                <FactRow k="Status" v={facts.status} />
              )}
              {facts.releaseDate && <FactRow k="Released" v={facts.releaseDate} mono />}
            </dl>
          </div>

          {(external.imdbId || external.homepage) && (
            <div className="rounded-[5px] border border-border bg-panel p-3">
              <PanelHeading>Links</PanelHeading>
              <ul className="space-y-1.5">
                {external.imdbId && (
                  <LinkRow href={`https://www.imdb.com/title/${external.imdbId}/`} label="IMDb" />
                )}
                {external.homepage && <LinkRow href={external.homepage} label="Official site" />}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {rankable && <ShelfRail mediaId={detail.id} title={detail.title} />}

      {/* Trailer dialog: iframe only mounted on open */}
      {detail.trailer && (
        <Dialog open={trailerOpen} onOpenChange={setTrailerOpen}>
          <DialogContent className="max-w-[960px] border-border bg-panel p-0">
            <DialogTitle className="sr-only">{detail.trailer.name}</DialogTitle>
            <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-[6px] bg-black">
              {trailerOpen && (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${detail.trailer.key}?autoplay=1&rel=0`}
                  title={detail.trailer.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full border-0"
                />
              )}
            </AspectRatio>
          </DialogContent>
        </Dialog>
      )}

      {/* Lightbox */}
      {detail.images && detail.images.length > 0 && (
        <Dialog open={lightboxIdx !== null} onOpenChange={(o) => !o && setLightboxIdx(null)}>
          <DialogContent className="max-w-[1200px] border-border bg-panel p-0">
            <DialogTitle className="sr-only">Still image</DialogTitle>
            {lightboxIdx !== null && (
              <img
                src={(detail.imagesOriginal ?? detail.images)[lightboxIdx]}
                alt=""
                decoding="async"
                className="h-auto w-full rounded-[6px] object-contain"
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </article>
  );
}

// One line under the read naming the lists this title holds a place on, each
// linked: "Breaking Bad is #1 on Best Crime Shows on Netflix, #1 on Best Drama
// Shows and #1 on Best Shows of 2008." The number is the position the linked
// page prints, so following the link shows where it came from. Same query as
// the closing rail, prefetched by the route so the links are in the HTML.
// Nothing renders while loading, on error, or for a title on no list.
//
// The line sits on the first screen, so it renders only when the lists were in
// hand at first render. When the route's lookup ran past its budget, a line
// arriving after hydration would shove the synopsis and everything under it
// down (a layout shift, counted in CLS) on every visit served that cached
// page. The caller keys this by title so the decision is made once per page.
function RanksOn({ mediaId, title }: { mediaId: string; title: string }) {
  const { data } = useQuery(appearsInQueryOptions(mediaId));
  const [inFirstRender] = useState(() => data !== undefined);
  const lists = inFirstRender ? (data?.ranksOn ?? []) : [];
  if (lists.length === 0) return null;
  return (
    <p className="mt-3 text-lg leading-relaxed text-text">
      {title} is{" "}
      {lists.map((p, i) => (
        <span key={p.slug}>
          {i > 0 && (i === lists.length - 1 ? " and " : ", ")}
          <span className="font-mono tabular-nums text-text-bright">#{p.rank}</span> on{" "}
          <Link
            to="/best/$slug"
            params={{ slug: p.slug }}
            className="text-text-bright underline decoration-border-strong underline-offset-2 transition-colors hover:text-primary"
          >
            {listLabel(p)}
          </Link>
        </span>
      ))}
      .
    </p>
  );
}

// The page closes on the shelf this title ranks in: the collection, the place
// it holds, and the titles either side of it carrying their rank numerals, so
// the order on screen is explained by what is on screen. Replaces the "Appears
// in" sidebar list — a reader who has decided no now has somewhere to go.
//
// Fail-soft: nothing renders while loading, on error, or for a title that has
// not earned a shelf spot. Those titles end the page where it ended before.
function ShelfRail({ mediaId, title }: { mediaId: string; title: string }) {
  const { data } = useQuery(appearsInQueryOptions(mediaId));
  const shelf = data?.shelf;
  // Two slots is not a shelf, and a rail of one card is a worse ending than
  // no rail at all.
  if (!shelf || shelf.neighbors.length < 3) return null;
  return (
    <section className="mx-auto max-w-content px-4 pb-16">
      <SectionHeading>
        <Link
          to="/best/$slug"
          params={{ slug: shelf.slug }}
          className="transition-colors hover:text-primary"
        >
          {shelf.title}
        </Link>
      </SectionHeading>
      <p className="mb-3 text-base text-text-muted">
        {title} is{" "}
        <span className="font-mono tabular-nums text-text-bright">
          #{shelf.rank} of {shelf.item_count}
        </span>
      </p>
      <ScrollRail className="gap-3">
        {shelf.neighbors.map((slot) => (
          <div key={slot.item.id} className="w-[118px] shrink-0 md:w-[132px]">
            <MediaCard
              item={slot.item}
              imgSizes="(min-width: 768px) 132px, 118px"
              posterOverlay={
                <span
                  aria-hidden="true"
                  className={
                    "font-mono text-2xl font-bold tabular-nums leading-none [text-shadow:0_2px_10px_rgba(0,0,0,0.95)] " +
                    (slot.rank === shelf.rank ? "text-primary" : "text-white/95")
                  }
                >
                  {slot.rank}
                </span>
              }
            />
          </div>
        ))}
      </ScrollRail>
    </section>
  );
}

// `mono` marks a value that is a number, a sum of money, or a date. Words
// (language, status) render in Archivo like everything else a person reads.
function FactRow({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-text-dim">{k}</dt>
      <dd
        className={
          "truncate text-text-bright" + (mono ? " font-mono text-[11px] tabular-nums" : "")
        }
      >
        {v}
      </dd>
    </div>
  );
}

function LinkRow({ href, label }: { href: string; label: string }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between rounded-[4px] border border-border px-2 py-1 text-[13px] font-semibold tracking-[-0.01em] text-text-muted hover:border-border-strong hover:text-text-bright"
      >
        <span>{label}</span>
        <ExternalLink className="h-3 w-3" />
      </a>
    </li>
  );
}

function DetailLoader() {
  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8">
      <div className="h-[300px] animate-pulse rounded-[8px] bg-panel md:h-[440px]" />
      <div className="mt-6 h-8 w-2/3 animate-pulse rounded bg-panel" />
      <div className="mt-3 h-4 w-1/3 animate-pulse rounded bg-panel" />
    </div>
  );
}

function DetailFetcher({
  type,
  id,
  episodeRatings,
  colorA,
  colorB,
}: {
  type: "movie" | "tv";
  id: string;
  episodeRatings?: EpisodeRating[];
  colorA?: string | null;
  colorB?: string | null;
}) {
  const { data } = useMediaDetail(type, id);
  return (
    <DetailInner detail={data} episodeRatings={episodeRatings} colorA={colorA} colorB={colorB} />
  );
}

function BackBar() {
  const router = useRouter();
  const goBack = () => {
    // Prefer a true browser-back so the filtered grid + scroll position are
    // restored; fall back to the homepage (which restores filters from storage).
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({ to: "/" });
    }
  };
  // Overlaid on the hero banner (absolute, below the top bar) instead of
  // occupying its own row — the old placement burned a full-width band of
  // vertical space just for one small button. Glassy treatment keeps it legible
  // over any backdrop.
  return (
    <div className="pointer-events-none absolute inset-x-0 top-12 z-20">
      <div className="mx-auto max-w-[1100px] px-4 pt-4">
        <button
          type="button"
          onClick={goBack}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-[5px] border border-white/20 bg-background/60 px-2.5 py-1.5 text-[13px] font-bold tracking-[-0.01em] text-text-bright backdrop-blur-md transition-colors hover:border-primary hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to browse
        </button>
      </div>
    </div>
  );
}

export function MediaDetail({
  mediaType,
  id,
  episodeRatings,
  colorA,
  colorB,
}: {
  mediaType: "movie" | "tv";
  id: string;
  /** TV only: stored per-episode ratings, loaded by the /tv/$id route. */
  episodeRatings?: EpisodeRating[];
  /** The title's stored poster colors (media.color_a / color_b), read by the
   *  route loader so the glow is in the server-rendered HTML. */
  colorA?: string | null;
  colorB?: string | null;
}) {
  return (
    <div className="relative min-h-screen bg-background">
      <BackBar />
      <main id="main">
        <Suspense fallback={<DetailLoader />}>
          <DetailFetcher
            type={mediaType}
            id={id}
            episodeRatings={episodeRatings}
            colorA={colorA}
            colorB={colorB}
          />
        </Suspense>
      </main>
    </div>
  );
}
